import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import get from 'lodash/get';
import set from 'lodash/set';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import MUIDataTable from 'mui-datatables';
import { Grid, Typography } from '@mui/material';
import UILink from '@mui/material/Link';
import { Flag } from '@mui/icons-material';

import {
  LAST_DEPLOY_FIELD,
  LAST_TRIP_FIELD,
  LAST_PICKUP_FIELD,
  longDateTimeFormat,
  TaskPriorityNames,
  TaskTypes,
  timeZoneHint,
  MISMATCH,
} from '../../../lib/utils';
import {
  VEHICLE_TYPE_URL_NAME,
  VEHICLE_BRAND_URL_NAME,
  VEHICLE_STATUS_URL_NAME,
  VEHICLE_BATTERY_GT_URL_NAME,
  VEHICLE_BATTERY_LT_URL_NAME,
} from '../../../lib/routes/routeHelper';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import BatteryLevel from '../../common/battery-level';

import { READ, VEHICLE_TRIPS_TABLE } from '../../../access';
import withUrlProperties from '../../common/url-props';

// map used to determine the index of a column; index used to set filters per-column
const TYPE_COL_NAME = 'Type';
const BRAND_COL_NAME = 'Brand';
const STATUS_COL_NAME = 'Status';
const PARTNER_STATUS_COL_NAME = ' Vehicle State';
const PARTNER_DEPLOYMENT_STATUS_COL_NAME = 'Deployment Status';
const PARTNER_POSSESSION_STATUS_COL_NAME = 'Possession Status';
const MAINTENANCE = 'Maintenance';
const NEEDS_MAINTENANCE = 'Needs maintenance';
const BATTERY_LEVEL_COL_NAME = 'Battery Level';
const SCOOTER_TYPE_COL_NAME = 'Scooter Type';

const LAST_COMMUNICATION = 'Last Communication';
const LAST_RIDER_SCAN = 'Last Rider Scan';
const HARDWARE = 'Hardware';
const LAST_MODIFIED = 'Last Modified';
const LAST_TRIP = 'Last Trip';
const LAST_DEPLOY = 'Last Deploy';
const LAST_PICKUP = 'Last Pickup';
const TRANSITION_DATE = 'Transition Date';

const COL_NAME_MAP = {
  [VEHICLE_TYPE_URL_NAME]: TYPE_COL_NAME,
  [VEHICLE_BRAND_URL_NAME]: BRAND_COL_NAME,
  [VEHICLE_STATUS_URL_NAME]: STATUS_COL_NAME,
  [VEHICLE_BATTERY_GT_URL_NAME]: { columnName: BATTERY_LEVEL_COL_NAME, filterIdx: 0 },
  [VEHICLE_BATTERY_LT_URL_NAME]: { columnName: BATTERY_LEVEL_COL_NAME, filterIdx: 1 },
};

const URGENT_TASK = 'Urgent';
const REGULAR_TASK = 'Regular';
const SEARCH_ATTEMPTS = 'Search Attempts';
const CELLULAR_NETWORK = 'Cellular Network';

// added to make HumanId column stick when scrolling
// table left to right: https://github.com/gregnb/mui-datatables/issues/236
const stickyStyle = {
  position: 'sticky',
  left: 0,
  background: 'white',
  zIndex: 101,
};

class BaseVehicleTable extends React.Component {
  componentDidUpdate(prevProps) {
    const { showStatusFilter, clearSelectedRows } = this.props;
    const { showStatusFilter: prevShowStatusFilter } = prevProps;

    // clear selections when switching from active statuses to inactive/lost statuses
    if (showStatusFilter !== prevShowStatusFilter) {
      clearSelectedRows();
    }
  }

  get columns() {
    const {
      market,
      currentWrangler,
      showStatusFilter,
      selectedFilters,
      primarySort,
      addToColumnData,
      setUrlColumnFilters,
      match,
    } = this.props;
    const tz = get(market, 'tz', 'America/New_York');
    const marketId = get(match, 'params.marketId');
    const can = get(currentWrangler, 'access.can', () => true);
    const idTitle = 'Vehicle';

    // the order of objects in columnData needs to match
    // the order of objects in data - both are referenced
    // on the render of the MUI datatable
    // columnData[0] is hidden from view, but contains the vehicleId
    // and is used to allow the onRowClick above to know what vehicle
    // it is referencing
    const columnData = [];
    columnData.push(
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: idTitle,
        options: {
          filter: false,
          setCellProps: () => ({ style: stickyStyle }),
        },
      },
      {
        name: PARTNER_STATUS_COL_NAME,
        options: {
          filter: false,
          customFilterListOptions: {
            render: (v) => `${PARTNER_STATUS_COL_NAME}: ${v}`,
          },
        },
      },
      {
        name: PARTNER_DEPLOYMENT_STATUS_COL_NAME,
        options: {
          filter: false,
          customFilterListOptions: {
            render: (v) => `${PARTNER_DEPLOYMENT_STATUS_COL_NAME}: ${v}`,
          },
        },
      },
      {
        name: PARTNER_POSSESSION_STATUS_COL_NAME,
        options: {
          filter: false,
          customFilterListOptions: {
            render: (v) => `${PARTNER_POSSESSION_STATUS_COL_NAME}: ${v}`,
          },
        },
      },
      {
        name: MAINTENANCE,
        options: {
          customFilterListOptions: {
            render: () => NEEDS_MAINTENANCE,
          },
          customBodyRender: (needsMaintenance) => needsMaintenance && MAINTENANCE,
          filter: false,
        },
      },
      {
        name: BATTERY_LEVEL_COL_NAME,
        options: {
          customBodyRender: (batteryLevel) => <BatteryLevel batteryLevel={batteryLevel} />,
          filter: false,
        },
      },
      {
        name: 'Pickup?',
        options: {
          customFilterListOptions: {
            render: () => NEEDS_MAINTENANCE,
          },
          customBodyRender: (urgent) => {
            if (urgent === URGENT_TASK) {
              return <Flag style={{ color: 'red' }} />;
            } else if (urgent === REGULAR_TASK) {
              return <Flag style={{ color: 'orange' }} />;
            } else {
              return '';
            }
          },
          filter: false,
        },
      },
      {
        name: 'Pickup Reason',
        options: {
          customBodyRender: (tasks) => {
            const { fetchOneTask } = this.props;
            if (tasks.length) {
              return tasks.map((task, i) => {
                const addComma = i < tasks.length - 1;
                return (
                  <Typography key={`${task._id}-pickup-link`} noWrap>
                    <UILink
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchOneTask(task._id);
                      }}
                    >
                      {`${task.pickupReason}${addComma ? ', ' : ''}`}
                    </UILink>
                  </Typography>
                );
              });
            } else {
              return '';
            }
          },
          filter: false,
          sort: false,
          download: false,
        },
      },
      {
      // column for download purposes only
        name: 'Pickup Reason - download',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: 'Inspect?',
        options: {
          customFilterListOptions: {
            render: () => NEEDS_MAINTENANCE,
          },
          customBodyRender: (urgent) => {
            if (urgent === URGENT_TASK) {
              return <Flag style={{ color: 'red' }} />;
            } else if (urgent === REGULAR_TASK) {
              return <Flag style={{ color: 'orange' }} />;
            } else {
              return '';
            }
          },
          filter: false,
        },
      },
      {
        name: 'Inspect Reason',
        options: {
          customBodyRender: (tasks) => {
            const { fetchOneTask } = this.props;
            if (tasks.length) {
              return tasks.map((task, i) => {
                const addComma = i < tasks.length - 1;
                return (
                  <Typography key={`${task._id}-inspect-link`} noWrap>
                    <UILink
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchOneTask(task._id);
                      }}
                    >
                      {`${task.inspectReason}${addComma ? ', ' : ''}`}
                    </UILink>
                  </Typography>
                );
              });
            } else {
              return '';
            }
          },
          filter: false,
          sort: false,
          download: false,
        },
      },
      {
      // column for download purposes only
        name: 'Inspect Reason - download',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: 'Assigned To',
      },
    );
    if (showStatusFilter) {
      columnData.push({
        name: TRANSITION_DATE,
        options: {
          customBodyRender: (utcDateString) => {
            if (!utcDateString || !utcDateString.length) return '';
            return moment.utc(utcDateString).tz(tz).format(longDateTimeFormat);
          },
        },
      });
    }
    columnData.push(
      {
        name: LAST_COMMUNICATION,
        options: {
          filter: false,
          hint: timeZoneHint(tz),
          customBodyRender: (date) => {
            if (!date) return '';
            return moment.tz(date, tz).format(longDateTimeFormat);
          },
        },
      },
      {
        name: LAST_RIDER_SCAN,
        options: {
          filter: false,
          hint: timeZoneHint(tz),
          customBodyRender: (date) => {
            if (!date) return '';
            return moment.tz(date, tz).format(longDateTimeFormat);
          },
        },
      },
      {
        name: CELLULAR_NETWORK,
        options: {
          filter: false,
        },
      },
      {
        name: HARDWARE,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => (
            <Grid>
              {data.map((r) => (
                <div style={{ borderRadius: 25, border: '1px solid #808080', marginTop: 2 }} key={r}>
                  <Typography style={{ textAlign: 'center' }}>{r}</Typography>
                </div>
              ))}
            </Grid>
          ),
        },
      },
      {
        name: SCOOTER_TYPE_COL_NAME,
        options: {
          filter: false,
        },
      },
      {
        name: LAST_MODIFIED,
        options: {
          filter: false,
          hint: timeZoneHint(tz),
          customBodyRender: (last) => {
            if (!last) return '';
            return moment.tz(last, tz).format(longDateTimeFormat);
          },
        },
      },
      {
        name: LAST_TRIP,
        options: {
          filter: false,
          hint: 'Date of Last Consumer Ride',
          customBodyRender: (last, tableMeta) => {
            if (can(READ, VEHICLE_TRIPS_TABLE)) {
              if (!last) return '';
              const vehicleId = get(tableMeta, 'rowData[0]');
              return (
                <UILink
                  component={Link}
                  to={`/operator/markets/${marketId}/vehicles/${vehicleId}/trips`}
                >
                  {moment.tz(last, tz).format(longDateTimeFormat)}
                </UILink>
              );
            }
            return '';
          },
        },
      },
      {
        name: LAST_PICKUP,
        options: {
          filter: false,
          hint: 'Last pickup task completed in the past 2 weeks',
          customBodyRender: (last) => {
            if (!last) return '';
            return moment.tz(last, tz).format(longDateTimeFormat);
          },
        },
      },
      {
        name: LAST_DEPLOY,
        options: {
          filter: false,
          hint: 'Last Drop Off Task in the past 2 weeks',
          customBodyRender: (last) => {
            if (!last) return '';
            return moment.tz(last, tz).format(longDateTimeFormat);
          },
        },
      },
      {
        // column for download purposes only
        name: 'Lat/Lng',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        // column for advanced filter use only
        name: SEARCH_ATTEMPTS,
        options: {
          filter: false,
          display: 'excluded',
        },
      },
    );

    return setUrlColumnFilters(addToColumnData(columnData, selectedFilters, primarySort));
  }

  get data() {
    const {
      vehicles,
      vehicleActivities,
      showStatusFilter,
      wranglersById,
    } = this.props;

    return vehicles.map((vehicle) => {
      const tasks = get(vehicle, 'tasks', []);
      const pickupTasks = tasks.filter((t) => t.task === TaskTypes.PICK_UP && t.pickupReason);
      const hasPickupTasks = pickupTasks.length > 0;
      const hasUrgentPickupTask = pickupTasks.find((t) => t.priority === TaskPriorityNames.URGENT);

      const inspectTasks = tasks.filter((t) => t.task === TaskTypes.INSPECT && t.inspectReason);
      const hasInspectTasks = inspectTasks.length > 0;
      const hasUrgentInspectTask = inspectTasks.find(
        (t) => t.priority === TaskPriorityNames.URGENT,
      );

      let pickupString;
      if (hasUrgentPickupTask) {
        pickupString = URGENT_TASK;
      } else if (hasPickupTasks) {
        pickupString = REGULAR_TASK;
      }

      let inspectString;
      if (hasUrgentInspectTask) {
        inspectString = URGENT_TASK;
      } else if (hasInspectTasks) {
        inspectString = REGULAR_TASK;
      }

      const assigneesObj = {};
      pickupTasks.concat(inspectTasks).forEach((task) => {
        const firstAssignedId = get(task, 'assignedToIds[0]');
        if (firstAssignedId) {
          const wrangler = get(wranglersById, firstAssignedId);
          const firstName = get(wrangler, 'firstName', '');
          const lastName = get(wrangler, 'lastName', '');
          set(assigneesObj, `${firstName} ${lastName}`, true);
        }
      });
      const assigneesString = Object.keys(assigneesObj).join(', ');

      const hardwareArray = [];
      if (get(vehicle, 'seatInstalled') === true) hardwareArray.push('Seat');
      const coords = get(vehicle, 'geo.geoJson.coordinates', []);
      const coordsString = `[${coords[1]}, ${coords[0]}]`;
      const getSearchAttempts = get(vehicle, 'searchAttempts', []);
      const searchAttempts = getSearchAttempts.length;
      const mismatchState = get(vehicle, 'hasStateMismatch', false);
      const mismatchStatus = mismatchState ? MISMATCH : '';

      const data = [
        get(vehicle, '_id', ''),
        get(vehicle, 'humanId', ''),
        mismatchStatus || get(vehicle, 'extStatus', ''),
        get(vehicle, 'extDeploymentStatus', ''),
        get(vehicle, 'extPossessionStatus', ''),
        get(vehicle, 'needsMaintenance', false),
        get(vehicle, 'batteryLevel', ''),
        pickupString,
        pickupTasks,
        pickupTasks.map((t) => t.pickupReason).join(', '), // download only column
        inspectString,
        inspectTasks,
        inspectTasks.map((t) => t.inspectReason).join(', '), // download only column
        assigneesString,
      ];
      if (showStatusFilter) data.push(get(vehicle, 'transitionDate', ''));
      data.push(
        get(vehicle, 'geoUpdatedAt', ''),
        get(vehicle, 'lastScanTime', ''),
        get(vehicle, 'lastReportedNetwork', ''),
        hardwareArray,
        get(vehicle, 'scooterType', ''),
        get(vehicle, 'extLastModified', ''),
        get(vehicleActivities, `${vehicle._id}.${LAST_TRIP_FIELD}`, ''), // lastTrip
        get(vehicleActivities, `${vehicle._id}.${LAST_PICKUP_FIELD}`, ''), // lastPickup
        get(vehicleActivities, `${vehicle._id}.${LAST_DEPLOY_FIELD}`, ''), // lastDeploy
        coordsString, // download only column
        searchAttempts, // for advanced search only
      );
      return data;
    });
  }

  get tableOptions() {
    const {
      tableOptions: basicOptions, vehicles, selectedRows, handleViewVehicle, handleRowsSelected,
    } = this.props;

    basicOptions.selectableRows = 'multiple';
    basicOptions.rowsSelected = selectedRows;
    basicOptions.search = false;
    basicOptions.filter = false;

    basicOptions.onRowClick = (rowData) => {
      const selectedVehicle = vehicles.filter((v) => v._id === rowData[0]);
      handleViewVehicle(selectedVehicle[0]);
    };

    const previousOnFilterChange = basicOptions.onFilterChange;
    basicOptions.onFilterChange = (changedColumn, filterList) => {
      // if (shouldApplyURLFilters) this.setState(() => ({ shouldApplyURLFilters: false }));
      if (previousOnFilterChange) previousOnFilterChange(changedColumn, filterList);
    };

    basicOptions.onRowSelectionChange = (row, allSelected) => {
      const selectedIndices = allSelected.map((item) => item.dataIndex);
      const selectedVehicles = filter(
        vehicles,
        (vehicle, index) => includes(selectedIndices, index),
      );
      const selectedVehiclesIds = selectedVehicles.map((vehicle) => vehicle._id);
      handleRowsSelected(selectedIndices, selectedVehiclesIds);
    };

    return basicOptions;
  }

  render() {
    const {
      getTitleFromData,
    } = this.props;
    return (
      <MUIDataTable
        title={getTitleFromData('Vehicles', this.data)}
        columns={this.columns}
        data={this.data}
        options={this.tableOptions}
      />
    );
  }
}

const VehicleDataTable = withUrlProperties(
  withPersistentSettings(
    withRouter(BaseVehicleTable),
    'VehicleTable',
  ),
  COL_NAME_MAP,
);
export { VehicleDataTable, BaseVehicleTable };
export default VehicleDataTable;
